import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../commons/Auth';
import { useLoading } from '../../commons/Loading';
import { useTranslation } from 'react-i18next';
import { useAlert } from 'react-alert';
import {
  NumberParam,
  StringParam,
  useQueryParams,
  withDefault,
} from 'use-query-params';
import { Link, Route, Switch, Redirect } from 'react-router-dom';
import BurgerMenu from '../../commons/BurgerMenu';
import Header from '../../commons/Header';
import variables from '../../variables';
import { Nav, NavItem, NavLink } from 'reactstrap';
import classNames from 'classnames';
import Footer from '../../commons/Footer';
import './ProfileDetail.scss';
import './TabsProfile.scss';
import MySignalsCondos from './SignalsCondo';
import { ReactComponent as IcnDocument } from '../../images/icn_document.svg';
import { withRouter } from 'react-router-dom';
import MySignalsSpecialJobs from './SignalsSpecialJobs';
import MySignalsRentalsSales from './SignalsRentalsSales';

const { REACT_APP_DEFAULT_PAGINATION_LIMIT = 50 } = process.env;

const MySignalsSent = ({ profile, match = {}, location }) => {
  const [t] = useTranslation('SIGNALS');
  const { showLoading, hideLoading } = useLoading();
  const alert = useAlert();
  const [{ newToken, newIsAdmin } = {}] = useAuth();

  const { url = '' } = match;
  const { pathname = '', state = {} } = location;
  const tab = pathname.split('/').pop();

  const [signals, setSignals] = useState([]);
  const [total, setTotal] = useState(0);

  const [query, setQuery] = useQueryParams({
    admin: withDefault(StringParam, ''),
    signalman: withDefault(StringParam, ''),
    condo: withDefault(StringParam, ''),
    accountabilityYear: NumberParam,
    accountabilityMonth: NumberParam,
    signalTypeCode: withDefault(StringParam, 'CONDO'),
    notes: withDefault(StringParam, ''),
    limit: withDefault(NumberParam, +REACT_APP_DEFAULT_PAGINATION_LIMIT),
    offset: NumberParam,
  });

  const {
    admin,
    signalman,
    condo,
    accountabilityYear,
    accountabilityMonth,
    signalTypeCode,
    notes,
    limit,
    offset,
  } = query;

  const fetchSignals = async () => {
    showLoading();

    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/signals/sent/${profile.type !== 'ADMIN_COLLAB' ? profile._id : profile.adminId}`,
      {
        headers: { Authorization: newToken },
        params: {
          admin,
          signalman,
          condo,
          notes,
          signalTypeCode,
          accountabilityYear,
          accountabilityMonth,
          limit,
          offset,
        },
      }
    );

    hideLoading();

    if (response instanceof Error) {
      return alert.error(t('COMMON:GENERIC_ERROR'));
    }

    const { data = [], headers = {} } = response;
    setSignals(data.signals || []);
    setTotal(+headers['x-total-count']);
  };

  function onFilter(queryDiff) {
    if (
      (queryDiff.admin && queryDiff.admin !== admin) ||
      (admin && queryDiff.admin === '') ||
      (queryDiff.signalman && queryDiff.signalman !== signalman) ||
      (signalman && queryDiff.signalman === '') ||
      (queryDiff.condo && queryDiff.condo !== condo) ||
      (condo && queryDiff.condo === '') ||
      (queryDiff.notes && queryDiff.notes !== notes) ||
      (notes && queryDiff.notes === '') ||
      (queryDiff.accountabilityYear &&
        queryDiff.accountabilityYear !== accountabilityYear) ||
      (accountabilityYear && queryDiff.accountabilityYear === '') ||
      (queryDiff.accountabilityMonth &&
        queryDiff.accountabilityMonth !== accountabilityMonth) ||
      (accountabilityMonth && queryDiff.accountabilityMonth === '') ||
      (queryDiff.signalTypeCode &&
        queryDiff.signalTypeCode !== signalTypeCode) ||
      (signalTypeCode && queryDiff.signalTypeCode === '') ||
      (queryDiff.limit && queryDiff.limit !== limit)
    ) {
      queryDiff.offset = 0; // return to first page
    }

    setQuery(queryDiff);
  }

  useEffect(() => {
    fetchSignals();
  }, [
    admin,
    signalman,
    condo,
    notes,
    signalTypeCode,
    accountabilityYear,
    accountabilityMonth,
    limit,
    offset,
  ]);

  if (pathname === url) {
    return <Redirect to={`${url}/condos`} />;
  }

  return (
    <div className="App">
      <BurgerMenu />

      <div className="corpo">
        <section>
          <Header
            title={t('PAGES:MY_SIGNALS_SENT')}
            icon={<IcnDocument fill={variables.white} />}
          />
        </section>
        <section>
          <div className="m-tb-20">
            <Nav tabs>
              <NavItem>
                <NavLink
                  tag={Link}
                  to={`${match.url}/condos?signalTypeCode=CONDO`}
                  className={classNames({ active: tab === 'condos' })}
                >
                  {t('PAGES:SIGNALS_CONDO_TAB')}
                </NavLink>
              </NavItem>

              <NavItem>
                <NavLink
                  tag={Link}
                  to={`${match.url}/special-jobs?signalTypeCode=SPECIAL_JOBS`}
                  className={classNames({ active: tab === 'special-jobs' })}
                >
                  {t('PAGES:SIGNALS_SPECIAL_JOBS_TAB')}
                </NavLink>
              </NavItem>

              <NavItem>
                <NavLink
                  tag={Link}
                  to={`${match.url}/rentals-sales?signalTypeCode=RENTALS_SALES`}
                  className={classNames({ active: tab === 'rentals-sales' })}
                >
                  {t('PAGES:SIGNALS_RENTALS_SALES_TAB')}
                </NavLink>
              </NavItem>
            </Nav>

            <Switch>
              <Route
                path={`${match.url}/condos`}
                component={(props) => (
                  <MySignalsCondos
                    signals={signals}
                    query={query}
                    total={total}
                    onFilter={onFilter}
                    {...props}
                  />
                )}
              />

              <Route
                path={`${match.url}/special-jobs`}
                component={(props) => (
                  <MySignalsSpecialJobs
                    signals={signals}
                    query={query}
                    total={total}
                    onFilter={onFilter}
                    {...props}
                  />
                )}
              />

              <Route
                path={`${match.url}/rentals-sales`}
                component={(props) => (
                  <MySignalsRentalsSales
                    signals={signals}
                    query={query}
                    total={total}
                    onFilter={onFilter}
                    {...props}
                  />
                )}
              />
            </Switch>
          </div>
        </section>
        <Footer />
      </div>
    </div>
  );
};

export default withRouter(MySignalsSent);
